/**
 * PFM (Personal Finance Management) Categories
 * Based on Plaid's PFCv2 Detailed categorization
 * Source: pfc-taxonomy-all.csv (PFCv2 is a superset of PFCv1)
 */

export const PFM_CATEGORIES = {
  // INCOME
  INCOME: {
    CHILD_SUPPORT: 'INCOME_CHILD_SUPPORT',
    DIVIDENDS: 'INCOME_DIVIDENDS',
    GIG_ECONOMY: 'INCOME_GIG_ECONOMY',
    INTEREST_EARNED: 'INCOME_INTEREST_EARNED',
    LONG_TERM_DISABILITY: 'INCOME_LONG_TERM_DISABILITY',
    MILITARY: 'INCOME_MILITARY',
    RENTAL: 'INCOME_RENTAL',
    RETIREMENT_PENSION: 'INCOME_RETIREMENT_PENSION',
    SALARY: 'INCOME_SALARY',
    TAX_REFUND: 'INCOME_TAX_REFUND',
    UNEMPLOYMENT: 'INCOME_UNEMPLOYMENT',
    OTHER: 'INCOME_OTHER',
  },

  // LOAN_DISBURSEMENTS
  LOAN_DISBURSEMENTS: {
    AUTO: 'LOAN_DISBURSEMENTS_AUTO',
    BNPL: 'LOAN_DISBURSEMENTS_BNPL',
    CASH_ADVANCES: 'LOAN_DISBURSEMENTS_CASH_ADVANCES',
    EWA: 'LOAN_DISBURSEMENTS_EWA',
    MORTGAGE: 'LOAN_DISBURSEMENTS_MORTGAGE',
    PERSONAL: 'LOAN_DISBURSEMENTS_PERSONAL',
    STUDENT: 'LOAN_DISBURSEMENTS_STUDENT',
    OTHER: 'LOAN_DISBURSEMENTS_OTHER_DISBURSEMENT',
  },

  // LOAN_PAYMENTS
  LOAN_PAYMENTS: {
    CAR_PAYMENT: 'LOAN_PAYMENTS_CAR_PAYMENT',
    CASH_ADVANCES: 'LOAN_PAYMENTS_CASH_ADVANCES',
    CREDIT_CARD_PAYMENT: 'LOAN_PAYMENTS_CREDIT_CARD_PAYMENT',
    EWA: 'LOAN_PAYMENTS_EWA',
    MORTGAGE_PAYMENT: 'LOAN_PAYMENTS_MORTGAGE_PAYMENT',
    PERSONAL_LOAN_PAYMENT: 'LOAN_PAYMENTS_PERSONAL_LOAN_PAYMENT',
    STUDENT_LOAN_PAYMENT: 'LOAN_PAYMENTS_STUDENT_LOAN_PAYMENT',
    OTHER_PAYMENT: 'LOAN_PAYMENTS_OTHER_PAYMENT',
  },

  // TRANSFER_IN
  TRANSFER_IN: {
    ACCOUNT_TRANSFER: 'TRANSFER_IN_ACCOUNT_TRANSFER',
    DEPOSIT: 'TRANSFER_IN_DEPOSIT',
    INVESTMENT_AND_RETIREMENT_FUNDS: 'TRANSFER_IN_INVESTMENT_AND_RETIREMENT_FUNDS',
    SAVINGS: 'TRANSFER_IN_SAVINGS',
    WIRE: 'TRANSFER_IN_WIRE',
    OTHER: 'TRANSFER_IN_OTHER_TRANSFER_IN',
  },

  // TRANSFER_OUT
  TRANSFER_OUT: {
    ACCOUNT_TRANSFER: 'TRANSFER_OUT_ACCOUNT_TRANSFER',
    INVESTMENT_AND_RETIREMENT_FUNDS: 'TRANSFER_OUT_INVESTMENT_AND_RETIREMENT_FUNDS',
    SAVINGS: 'TRANSFER_OUT_SAVINGS',
    WIRE: 'TRANSFER_OUT_WIRE',
    WITHDRAWAL: 'TRANSFER_OUT_WITHDRAWAL',
    OTHER: 'TRANSFER_OUT_OTHER_TRANSFER_OUT',
  },

  // BANK_FEES
  BANK_FEES: {
    ATM_FEES: 'BANK_FEES_ATM_FEES',
    INSUFFICIENT_FUNDS: 'BANK_FEES_INSUFFICIENT_FUNDS',
    INTEREST_CHARGE: 'BANK_FEES_INTEREST_CHARGE',
    FOREIGN_TRANSACTION_FEES: 'BANK_FEES_FOREIGN_TRANSACTION_FEES',
    OVERDRAFT_FEES: 'BANK_FEES_OVERDRAFT_FEES',
    LATE_FEES: 'BANK_FEES_LATE_FEES',
    CASH_ADVANCE: 'BANK_FEES_CASH_ADVANCE',
    OTHER: 'BANK_FEES_OTHER_BANK_FEES',
  },

  // ENTERTAINMENT
  ENTERTAINMENT: {
    CASINOS_AND_GAMBLING: 'ENTERTAINMENT_CASINOS_AND_GAMBLING',
    MUSIC_AND_AUDIO: 'ENTERTAINMENT_MUSIC_AND_AUDIO',
    SPORTING_EVENTS_AMUSEMENT_PARKS_AND_MUSEUMS: 'ENTERTAINMENT_SPORTING_EVENTS_AMUSEMENT_PARKS_AND_MUSEUMS',
    TV_AND_MOVIES: 'ENTERTAINMENT_TV_AND_MOVIES',
    VIDEO_GAMES: 'ENTERTAINMENT_VIDEO_GAMES',
    OTHER: 'ENTERTAINMENT_OTHER_ENTERTAINMENT',
  },

  // FOOD_AND_DRINK
  FOOD_AND_DRINK: {
    BEER_WINE_AND_LIQUOR: 'FOOD_AND_DRINK_BEER_WINE_AND_LIQUOR',
    COFFEE: 'FOOD_AND_DRINK_COFFEE', // For "Skip the Latte" targeting
    FAST_FOOD: 'FOOD_AND_DRINK_FAST_FOOD',
    GROCERIES: 'FOOD_AND_DRINK_GROCERIES',
    RESTAURANT: 'FOOD_AND_DRINK_RESTAURANT',
    VENDING_MACHINES: 'FOOD_AND_DRINK_VENDING_MACHINES',
    OTHER: 'FOOD_AND_DRINK_OTHER_FOOD_AND_DRINK',
  },

  // GENERAL_MERCHANDISE
  GENERAL_MERCHANDISE: {
    BOOKSTORES_AND_NEWSSTANDS: 'GENERAL_MERCHANDISE_BOOKSTORES_AND_NEWSSTANDS',
    CLOTHING_AND_ACCESSORIES: 'GENERAL_MERCHANDISE_CLOTHING_AND_ACCESSORIES',
    CONVENIENCE_STORES: 'GENERAL_MERCHANDISE_CONVENIENCE_STORES',
    DEPARTMENT_STORES: 'GENERAL_MERCHANDISE_DEPARTMENT_STORES',
    DISCOUNT_STORES: 'GENERAL_MERCHANDISE_DISCOUNT_STORES',
    ELECTRONICS: 'GENERAL_MERCHANDISE_ELECTRONICS',
    GIFTS_AND_NOVELTIES: 'GENERAL_MERCHANDISE_GIFTS_AND_NOVELTIES',
    OFFICE_SUPPLIES: 'GENERAL_MERCHANDISE_OFFICE_SUPPLIES',
    ONLINE_MARKETPLACES: 'GENERAL_MERCHANDISE_ONLINE_MARKETPLACES',
    PET_SUPPLIES: 'GENERAL_MERCHANDISE_PET_SUPPLIES',
    SPORTING_GOODS: 'GENERAL_MERCHANDISE_SPORTING_GOODS',
    SUPERSTORES: 'GENERAL_MERCHANDISE_SUPERSTORES',
    TOBACCO_AND_VAPE: 'GENERAL_MERCHANDISE_TOBACCO_AND_VAPE',
    OTHER: 'GENERAL_MERCHANDISE_OTHER_GENERAL_MERCHANDISE',
  },

  // HOME_IMPROVEMENT
  HOME_IMPROVEMENT: {
    FURNITURE: 'HOME_IMPROVEMENT_FURNITURE',
    HARDWARE: 'HOME_IMPROVEMENT_HARDWARE',
    REPAIR_AND_MAINTENANCE: 'HOME_IMPROVEMENT_REPAIR_AND_MAINTENANCE',
    SECURITY: 'HOME_IMPROVEMENT_SECURITY',
    OTHER: 'HOME_IMPROVEMENT_OTHER_HOME_IMPROVEMENT',
  },

  // MEDICAL
  MEDICAL: {
    DENTAL_CARE: 'MEDICAL_DENTAL_CARE',
    EYE_CARE: 'MEDICAL_EYE_CARE',
    NURSING_CARE: 'MEDICAL_NURSING_CARE',
    PHARMACIES_AND_SUPPLEMENTS: 'MEDICAL_PHARMACIES_AND_SUPPLEMENTS',
    PRIMARY_CARE: 'MEDICAL_PRIMARY_CARE',
    VETERINARY_SERVICES: 'MEDICAL_VETERINARY_SERVICES',
    OTHER: 'MEDICAL_OTHER_MEDICAL',
  },

  // PERSONAL_CARE
  PERSONAL_CARE: {
    GYMS_AND_FITNESS_CENTERS: 'PERSONAL_CARE_GYMS_AND_FITNESS_CENTERS',
    HAIR_AND_BEAUTY: 'PERSONAL_CARE_HAIR_AND_BEAUTY',
    LAUNDRY_AND_DRY_CLEANING: 'PERSONAL_CARE_LAUNDRY_AND_DRY_CLEANING',
    OTHER: 'PERSONAL_CARE_OTHER_PERSONAL_CARE',
  },

  // GENERAL_SERVICES
  GENERAL_SERVICES: {
    ACCOUNTING_AND_FINANCIAL_PLANNING: 'GENERAL_SERVICES_ACCOUNTING_AND_FINANCIAL_PLANNING',
    AUTOMOTIVE: 'GENERAL_SERVICES_AUTOMOTIVE',
    CHILDCARE: 'GENERAL_SERVICES_CHILDCARE',
    CONSULTING_AND_LEGAL: 'GENERAL_SERVICES_CONSULTING_AND_LEGAL',
    EDUCATION: 'GENERAL_SERVICES_EDUCATION',
    INSURANCE: 'GENERAL_SERVICES_INSURANCE',
    POSTAGE_AND_SHIPPING: 'GENERAL_SERVICES_POSTAGE_AND_SHIPPING',
    STORAGE: 'GENERAL_SERVICES_STORAGE',
    OTHER: 'GENERAL_SERVICES_OTHER_GENERAL_SERVICES',
  },

  // GOVERNMENT_AND_NON_PROFIT
  GOVERNMENT_AND_NON_PROFIT: {
    DONATIONS: 'GOVERNMENT_AND_NON_PROFIT_DONATIONS',
    GOVERNMENT_DEPARTMENTS_AND_AGENCIES: 'GOVERNMENT_AND_NON_PROFIT_GOVERNMENT_DEPARTMENTS_AND_AGENCIES',
    TAX_PAYMENT: 'GOVERNMENT_AND_NON_PROFIT_TAX_PAYMENT',
    OTHER: 'GOVERNMENT_AND_NON_PROFIT_OTHER_GOVERNMENT_AND_NON_PROFIT',
  },

  // TRANSPORTATION
  TRANSPORTATION: {
    BIKES_AND_SCOOTERS: 'TRANSPORTATION_BIKES_AND_SCOOTERS',
    GAS: 'TRANSPORTATION_GAS',
    PARKING: 'TRANSPORTATION_PARKING',
    PUBLIC_TRANSIT: 'TRANSPORTATION_PUBLIC_TRANSIT',
    TAXIS_AND_RIDE_SHARES: 'TRANSPORTATION_TAXIS_AND_RIDE_SHARES', // For rideshare spending targeting
    TOLLS: 'TRANSPORTATION_TOLLS',
    OTHER: 'TRANSPORTATION_OTHER_TRANSPORTATION',
  },

  // TRAVEL
  TRAVEL: {
    FLIGHTS: 'TRAVEL_FLIGHTS',
    LODGING: 'TRAVEL_LODGING',
    RENTAL_CARS: 'TRAVEL_RENTAL_CARS',
    OTHER: 'TRAVEL_OTHER_TRAVEL',
  },

  // RENT_AND_UTILITIES
  RENT_AND_UTILITIES: {
    GAS_AND_ELECTRICITY: 'RENT_AND_UTILITIES_GAS_AND_ELECTRICITY',
    INTERNET_AND_CABLE: 'RENT_AND_UTILITIES_INTERNET_AND_CABLE',
    RENT: 'RENT_AND_UTILITIES_RENT',
    SEWAGE_AND_WASTE_MANAGEMENT: 'RENT_AND_UTILITIES_SEWAGE_AND_WASTE_MANAGEMENT',
    TELEPHONE: 'RENT_AND_UTILITIES_TELEPHONE',
    WATER: 'RENT_AND_UTILITIES_WATER',
    OTHER: 'RENT_AND_UTILITIES_OTHER_UTILITIES',
  },

  // OTHER
  OTHER: {
    OTHER: 'OTHER_OTHER',
  },
} as const;

// Flattened array of all PFM categories for easy selection
export const ALL_PFM_CATEGORIES = Object.values(PFM_CATEGORIES).flatMap(group =>
  Object.values(group)
) as string[];

// Convert a code segment to title case with acronym preservation
const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .replace(/\bBnpl\b/g, 'BNPL')
    .replace(/\bEwa\b/g, 'EWA')
    .replace(/\bAtm\b/g, 'ATM')
    .replace(/\bTv\b/g, 'TV');
};

// Pre-build display name lookup: primary codes → full title case,
// detailed codes → primary prefix stripped then title case
const DISPLAY_NAMES = new Map<string, string>();
for (const [primaryKey, group] of Object.entries(PFM_CATEGORIES)) {
  DISPLAY_NAMES.set(primaryKey, toTitleCase(primaryKey));
  const prefix = primaryKey + '_';
  for (const code of Object.values(group)) {
    const strCode = code as string;
    const suffix = strCode.startsWith(prefix) ? strCode.slice(prefix.length) : strCode;
    DISPLAY_NAMES.set(strCode, toTitleCase(suffix));
  }
}

// Get a human-readable display name for any PFM category code.
// Primary codes return their full title case (e.g. "Food And Drink").
// Detailed codes strip the primary prefix (e.g. "Fast Food", "Flights").
// If overrides map is provided, check it first (admin-managed display names).
export const getPfmCategoryDisplayName = (
  category: string,
  overrides?: Record<string, string>,
): string => {
  if (overrides && overrides[category]) {
    return overrides[category];
  }
  return DISPLAY_NAMES.get(category) ?? toTitleCase(category);
};

// Groups excluded from user-facing category picker (non-assignable)
const EXCLUDED_GROUPS = ['INCOME', 'LOAN_DISBURSEMENTS', 'TRANSFER_IN', 'TRANSFER_OUT'] as const;

/**
 * Grouped PFM categories for the category picker UI.
 * Each group has a primary label/code and an array of detailed sub-categories.
 */
export const PFM_CATEGORY_GROUPS: {
  primary: string;
  primaryCode: string;
  categories: { code: string; label: string }[];
}[] = Object.entries(PFM_CATEGORIES)
  .filter(([key]) => !(EXCLUDED_GROUPS as readonly string[]).includes(key))
  .map(([key, group]) => ({
    primary: getPfmCategoryDisplayName(key),
    primaryCode: key,
    categories: Object.values(group).map((code) => ({
      code: code as string,
      label: getPfmCategoryDisplayName(code as string),
    })),
  }));

/**
 * Derive the primary category from a detailed category code.
 * E.g. "FOOD_AND_DRINK_COFFEE" -> "FOOD_AND_DRINK"
 */
/**
 * Build the Plaid category icon URL for a given primary category.
 * Pattern: https://plaid-category-icons.plaid.com/PFC_{PRIMARY}.png
 */
export const getPlaidCategoryIconUrl = (primaryCategory: string): string => {
  return `https://plaid-category-icons.plaid.com/PFC_${primaryCategory}.png`;
};

export const getPrimaryFromDetailed = (detailed: string): string => {
  for (const [primaryKey, group] of Object.entries(PFM_CATEGORIES)) {
    for (const code of Object.values(group)) {
      if (code === detailed) return primaryKey;
    }
  }
  // Fallback: strip the last segment (best-effort)
  const parts = detailed.split('_');
  if (parts.length > 1) {
    parts.pop();
    return parts.join('_');
  }
  return detailed;
};
